import {
    Navigate,
    Outlet,
    type SearchSchemaInput,
    createRootRoute,
    createRoute,
    createRouter,
    stripSearchParams,
    useLocation,
} from "@tanstack/react-router";
import { Spinner } from "components/elements";
import { CompassEventsLayout } from "components/layout/CompassEventsLayout";
import { EventsLayout } from "components/layout/EventsLayout";
import { Page } from "components/layout/Page";
import { PortalLayout } from "components/layout/PortalLayout";
import { SocialLayout } from "components/layout/SocialLayout";
import { VendorLayout } from "components/layout/VendorLayout";
import { useTrackUserLogin } from "hooks/useAnalytics";
import { useIsUserLoggedIn } from "hooks/useIsUserLoggedIn";
import { redirectManager } from "lib/redirectManager";
import { trpc } from "lib/trpc";
import { Suspense, lazy } from "react";
// Auth screens — kept eager (small, needed before JS fully loads)
import { ForgotPassword } from "screens/ForgotPassword";
import { Login } from "screens/Login";
import { Logout } from "screens/Logout";
import { Masquerade } from "screens/Masquerade";
import { NotFound } from "screens/NotFound";
import { ResetPassword } from "screens/ResetPassword";
import { z } from "zod";
import {
    type FavoritesSearchParams,
    FavoritesSearchSchema,
    PRIVATE_FAVORITE_TYPES,
    PUBLIC_FAVORITE_TYPES,
    type TrendingSearchParams,
    TrendingSearchSchema,
} from "@compass/shared/schema/favorite";
import {
    ChartsListSchema,
    ChartsList as ChartsListSchemaType,
    DiscussionsListSchema,
    DiscussionsList as DiscussionsListSchemaType,
    DocumentsListSchema,
    DocumentsList as DocumentsListSchemaType,
    VendorListSchema,
    VendorList as VendorListSchemaType,
} from "@compass/shared/types";

// Authenticated screens — lazy loaded (all render inside portalRoute's Suspense boundary)
const AuthorPosts = lazy(() =>
    import("screens/AuthorPosts").then((m) => ({ default: m.AuthorPosts })),
);
const Documents = lazy(() => import("screens/Documents").then((m) => ({ default: m.Documents })));
const ResourceCategory = lazy(() =>
    import("screens/ResourceCategory").then((m) => ({ default: m.ResourceCategory })),
);
const ResourcePost = lazy(() =>
    import("screens/ResourcePost").then((m) => ({ default: m.ResourcePost })),
);
const ChartDetailScreen = lazy(() =>
    import("screens/charts/ChartDetailScreen").then((m) => ({ default: m.ChartDetailScreen })),
);
const ChartsListScreen = lazy(() =>
    import("screens/charts/ChartsListScreen").then((m) => ({ default: m.ChartsListScreen })),
);
const CreateChartScreen = lazy(() =>
    import("screens/charts/CreateChartScreen").then((m) => ({ default: m.CreateChartScreen })),
);
const CompassEvents = lazy(() =>
    import("screens/compassEvents/CompassEvents").then((m) => ({ default: m.CompassEvents })),
);
const CompassEventJoin = lazy(() =>
    import("screens/compassEvents/CompassEventJoin").then((m) => ({ default: m.CompassEventJoin })),
);
const Meetings = lazy(() =>
    import("screens/meetings/Meetings").then((m) => ({ default: m.Meetings })),
);
const MeetingsLayout = lazy(() =>
    import("screens/meetings/MeetingsLayout").then((m) => ({ default: m.MeetingsLayout })),
);
const PreviousMeetings = lazy(() =>
    import("screens/meetings/PreviousMeetings").then((m) => ({ default: m.PreviousMeetings })),
);
const MeetingSeriesDetail = lazy(() =>
    import("screens/meetings/MeetingSeriesDetail").then((m) => ({
        default: m.MeetingSeriesDetail,
    })),
);
const MeetingInstanceDetail = lazy(() =>
    import("screens/meetings/MeetingInstanceDetail").then((m) => ({
        default: m.MeetingInstanceDetail,
    })),
);
const AIDigest = lazy(() =>
    import("screens/digest/AIDigest").then((m) => ({ default: m.AIDigest })),
);
const AIDigestDetail = lazy(() =>
    import("screens/digest/AIDigestDetail").then((m) => ({ default: m.AIDigestDetail })),
);
const DiscussionMessage = lazy(() =>
    import("screens/discussions/DiscussionMessage").then((m) => ({ default: m.DiscussionMessage })),
);
const DiscussionsList = lazy(() =>
    import("screens/discussions/List").then((m) => ({ default: m.DiscussionsList })),
);
const EventDetailPage = lazy(() =>
    import("screens/events").then((m) => ({ default: m.EventDetailPage })),
);
const EventJoin = lazy(() =>
    import("screens/events/EventJoin").then((m) => ({ default: m.EventJoin })),
);
const Events = lazy(() => import("screens/events/Events").then((m) => ({ default: m.Events })));
const Favorites = lazy(() =>
    import("screens/favorites/Favorites").then((m) => ({ default: m.Favorites })),
);
const FriendsTable = lazy(() => import("screens/friends/FriendsList"));
const Live = lazy(() => import("screens/live/Live").then((m) => ({ default: m.Live })));
const LiveJoin = lazy(() => import("screens/live/LiveJoin").then((m) => ({ default: m.LiveJoin })));
const Messages = lazy(() =>
    import("screens/messages/Messages").then((m) => ({ default: m.Messages })),
);
const NotificationsPage = lazy(() =>
    import("screens/notifications/NotificationsPage").then((m) => ({
        default: m.NotificationsPage,
    })),
);
const ResolveNotification = lazy(() =>
    import("screens/notifications/ResolveNotification").then((m) => ({
        default: m.ResolveNotification,
    })),
);
const EditProfile = lazy(() =>
    import("screens/profile/EditProfile").then((m) => ({ default: m.EditProfile })),
);
const Profile = lazy(() => import("screens/profile/Profile").then((m) => ({ default: m.Profile })));
const UserProfile = lazy(() =>
    import("screens/profile/UserProfile").then((m) => ({ default: m.UserProfile })),
);
const FullCategory = lazy(() =>
    import("screens/rss/FullCategory").then((m) => ({ default: m.FullCategory })),
);
const RssList = lazy(() => import("screens/rss/RssList").then((m) => ({ default: m.RssList })));
const RssArticle = lazy(() =>
    import("screens/rss/RssArticle").then((m) => ({ default: m.RssArticle })),
);
const SettingsNotifications = lazy(() =>
    import("screens/settings/SettingsNotifications").then((m) => ({
        default: m.SettingsNotifications,
    })),
);
const SettingsCalendars = lazy(() =>
    import("screens/settings/SettingsCalendars").then((m) => ({
        default: m.SettingsCalendars,
    })),
);
const SettingsSecurity = lazy(() =>
    import("screens/settings/SettingsSecurity").then((m) => ({ default: m.SettingsSecurity })),
);
const Social = lazy(() => import("screens/social/Social").then((m) => ({ default: m.Social })));
const SocialMain = lazy(() =>
    import("screens/social/SocialMain").then((m) => ({ default: m.SocialMain })),
);
const SocialRead = lazy(() =>
    import("screens/social/SocialRead").then((m) => ({ default: m.SocialRead })),
);
const Trending = lazy(() =>
    import("screens/trending/Trending").then((m) => ({ default: m.Trending })),
);
const BenchMark = lazy(() =>
    import("screens/vendors/BenchMark").then((m) => ({ default: m.BenchMark })),
);
const Budget = lazy(() => import("screens/vendors/Budget").then((m) => ({ default: m.Budget })));
const VendorDetail = lazy(() =>
    import("screens/vendors/VendorDetail").then((m) => ({ default: m.VendorDetail })),
);
const VendorList = lazy(() =>
    import("screens/vendors/VendorList").then((m) => ({ default: m.VendorList })),
);

const rootRoute = createRootRoute({
    component: function Root() {
        const isLoggedIn = useIsUserLoggedIn();
        const location = useLocation();

        const {
            data: user,
            isLoading: isUserLoading,
            isError: isUserError,
        } = trpc.users.me.useQuery(undefined, {
            enabled: isLoggedIn,
        });
        useTrackUserLogin(user?.email || null, user?.name || null);

        const currentPath = location.pathname;
        const isHomePage = ["/"].includes(currentPath);
        const isForgotPasswordPage = ["/forgot-password"].includes(currentPath);
        const isResetPasswordPage = currentPath.startsWith("/reset-password");
        const isLoginPage = currentPath === "/login";
        const isLogoutPage = currentPath === "/logout";
        // PRTL-1348: the masquerade exchange page authenticates ITSELF (via
        // the code in the URL fragment) — it must render for logged-out
        // visitors, and a logged-in admin must not be bounced off it either.
        const isMasqueradePage = currentPath === "/masquerade";

        // Token exists but query failed (expired/invalid token) — redirect to login
        if (isLoggedIn && isUserError) {
            return <Navigate to="/login" />;
        }

        if ((isLoginPage || isHomePage) && isLoggedIn) {
            // Check for a saved redirect from before login
            const savedPath = redirectManager.consume();
            if (savedPath) {
                return <Navigate to={savedPath as "/"} />;
            }
            if (user && user.isSocialEnabled) {
                return <Navigate to="/social" />;
            }
            if (user && user.isPrivateEnabled) {
                return <Navigate to="/discussions" />;
            }
            if (isUserLoading) {
                return (
                    <div className="flex min-h-screen items-center justify-center bg-linear-to-br from-[#e8f4f8] to-[#d4e9f0]">
                        <Spinner size="xl" />
                    </div>
                );
            }
        }

        if (
            !isLoginPage &&
            !isLogoutPage &&
            !isMasqueradePage &&
            !isLoggedIn &&
            !isForgotPasswordPage &&
            !isResetPasswordPage
        ) {
            // Save intended URL so we can restore it after login
            const fullPath = location.pathname + location.searchStr;
            redirectManager.save(fullPath);
            return <Navigate to="/login" />;
        }

        // if (isLoggedIn && user && isSocialPage && !user.isSocialEnabled) {
        // return <Navigate to="/" replace />;
        // return <div>Sorry, you don’t have access to this page.</div>;
        // }
        return <Page isLoggedIn={isLoggedIn} user={user} />;
    },
});

/**
 * Portal
 */

const portalRoute = createRoute({
    getParentRoute: () => rootRoute,
    id: "portal",
    component: () => (
        <Suspense
            fallback={
                <div className="flex min-h-screen items-center justify-center">
                    <Spinner size="xl" />
                </div>
            }
        >
            <PortalLayout />
        </Suspense>
    ),
});

// Redirect "/" to "/social"
// const homeRoute = createRoute({
//     getParentRoute: () => portalRoute,
//     path: "/",
//     component: function Home() {
//         const { data: user } = trpc.users.me.useQuery();
//
//         if (user?.isSocialEnabled) {
//             return <Navigate to="/social" replace />;
//         } else {
//             return <Navigate to="/documents" replace />;
//         }
//     },
// });

// v1 writes the validated search (defaults included) back into the URL. Strip values equal to
// their defaults so URLs stay as they were on the beta router (/documents, not
// /documents?showPublicFiles=true&showPrivateFiles=true). Defaults come from the schemas, via
// `search: { middlewares: [stripSearchParams(...)] }` on each route below.

// Favorites layout route — just an Outlet, no component of its own
export const favoritesRoute = createRoute({
    getParentRoute: () => portalRoute,
    path: "favorites",
});

// /favorites (index) — no default types
export const favoritesIndexRoute = createRoute({
    getParentRoute: () => favoritesRoute,
    path: "/",
    component: () => <Favorites />,
    validateSearch: FavoritesSearchSchema,
});

// /favorites/private — defaults to all private types, shows private nav
export const favoritesPrivateRoute = createRoute({
    getParentRoute: () => favoritesRoute,
    path: "private",
    component: () => <Favorites />,
    validateSearch: (
        search: z.input<typeof FavoritesSearchSchema> & SearchSchemaInput,
    ): FavoritesSearchParams => {
        const parsed = FavoritesSearchSchema.parse(search);
        return { ...parsed, types: parsed.types ?? PRIVATE_FAVORITE_TYPES.join(",") };
    },
    search: { middlewares: [stripSearchParams({ types: PRIVATE_FAVORITE_TYPES.join(",") })] },
});

// /favorites/public — defaults to all public types, shows social nav
export const favoritesPublicRoute = createRoute({
    getParentRoute: () => favoritesRoute,
    path: "public",
    component: () => <Favorites />,
    validateSearch: (
        search: z.input<typeof FavoritesSearchSchema> & SearchSchemaInput,
    ): FavoritesSearchParams => {
        const parsed = FavoritesSearchSchema.parse(search);
        return { ...parsed, types: parsed.types ?? PUBLIC_FAVORITE_TYPES.join(",") };
    },
    search: { middlewares: [stripSearchParams({ types: PUBLIC_FAVORITE_TYPES.join(",") })] },
});

export const trendingRoute = createRoute({
    getParentRoute: () => portalRoute,
    path: "trending",
    component: () => <Trending />,
    validateSearch: TrendingSearchSchema,
});

export const resourceCategoryRoute = createRoute({
    getParentRoute: () => portalRoute,
    path: "/c/$slug",
    component: () => <ResourceCategory />,
});

export const resourcePostRoute = createRoute({
    getParentRoute: () => portalRoute,
    path: "/p/$slug",
    component: () => <ResourcePost />,
});

export const authorPostsRoute = createRoute({
    getParentRoute: () => portalRoute,
    path: "/author/$id",
    component: () => <AuthorPosts />,
});

const discussionsRoute = createRoute({
    getParentRoute: () => portalRoute,
    path: "discussions",
    component: () => <Outlet />,
});

export const discussionsList = createRoute({
    getParentRoute: () => discussionsRoute,
    path: "/",
    component: () => <DiscussionsList />,
    validateSearch: DiscussionsListSchema,
});

// const discussionsNewRoute = createRoute({
//     getParentRoute: () => discussionsRoute,
//     path: "/new",
//     component: () => <DiscussionsNew />,
// });

export const discussionsReadRoute = createRoute({
    getParentRoute: () => discussionsRoute,
    path: "/$discussionId/$messageId",
    component: () => <DiscussionMessage />,
});

// export const discussionsReadMessageRoute = createRoute({
//     getParentRoute: () => discussionsReadRoute,
//     path: "/message/$messageId",
//     component: () => <DiscussionsRead />,
// });

export const documentsRoute = createRoute({
    getParentRoute: () => portalRoute,
    path: "documents",
    component: () => <Documents />,
    validateSearch: DocumentsListSchema,
    search: { middlewares: [stripSearchParams(DocumentsListSchema.parse({}))] },
});

export const settingsRoute = createRoute({
    getParentRoute: () => portalRoute,
    path: "settings",
    component: () => <Outlet />,
});

export const settingsSecurity = createRoute({
    getParentRoute: () => settingsRoute,
    path: "/security",
    component: () => <SettingsSecurity />,
});

export const settingsNotifications = createRoute({
    getParentRoute: () => settingsRoute,
    path: "/notifications",
    component: () => <SettingsNotifications />,
});

// PRTL-857: gated on calendarImports inside the screen, so the route stays mounted.
export const settingsCalendars = createRoute({
    getParentRoute: () => settingsRoute,
    path: "/calendars",
    component: () => <SettingsCalendars />,
});

export const vendorList = createRoute({
    getParentRoute: () => vendorRoute,
    path: "/",
    component: () => <VendorList />,
    validateSearch: VendorListSchema,
});

export const vendorDetail = createRoute({
    getParentRoute: () => vendorRoute,
    path: "/$id",
    component: () => <VendorDetail />,
});

export const friendList = createRoute({
    getParentRoute: () => friendRoute,
    path: "/",
    component: () => <FriendsTable />,
});

const friendRoute = createRoute({
    getParentRoute: () => portalRoute,
    path: "friends",
    component: () => <VendorLayout />,
});

export const eventsRoute = createRoute({
    getParentRoute: () => portalRoute,
    path: "events",
    component: () => <EventsLayout />,
});

const eventsListRoute = createRoute({
    getParentRoute: () => eventsRoute,
    path: "/list",
    component: () => <Events />,
});

export const eventsDetailRoute = createRoute({
    getParentRoute: () => eventsRoute,
    path: "/$id",
    component: () => <EventDetailPage />,
});

export const eventsJoinRoute = createRoute({
    getParentRoute: () => eventsRoute,
    path: "/$id/join",
    component: () => <EventJoin />,
});

// PRTL-1087: /private-events becomes a layout route so the calendar (index
// child) and the Zoom join screen (/$id/join child) can each render fullscreen
// without one stomping the other. Previously the calendar lived directly on
// this route and didn't render an <Outlet />, so any child route never got a
// chance to mount.
export const compassEventsRoute = createRoute({
    getParentRoute: () => portalRoute,
    path: "private-events",
    component: () => <CompassEventsLayout />,
});

export const compassEventsIndexRoute = createRoute({
    getParentRoute: () => compassEventsRoute,
    path: "/",
    component: () => <CompassEvents />,
});

export const compassEventsJoinRoute = createRoute({
    getParentRoute: () => compassEventsRoute,
    path: "/$id/join",
    component: () => <CompassEventJoin />,
});

// PRTL-852 (extended): dedicated /meetings hub. Shortcode-keyed URLs so
// they're human-friendly + the literal `series/` segment makes routing
// unambiguous (was previously /$seriesId vs /$seriesId/$instanceId, which
// TanStack Router couldn't disambiguate cleanly).
//
//   /meetings                                — index w/ today/upcoming/recent
//   /meetings/previous?page=&pageSize=        — paginated past archive
//   /meetings/series/{seriesShortCode}        — series detail
//   /meetings/{instanceShortCode}             — instance hub (auto-mounts embed)
// `showAll` is the superuser "see every meeting" toggle (default off = only the
// caller's own / created). Ignored server-side for non-superusers.
const MeetingsIndexSearchSchema = z.object({
    showAll: z.boolean().default(false),
});
export type MeetingsIndexSearchParams = z.infer<typeof MeetingsIndexSearchSchema>;

const PreviousMeetingsSearchSchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(5).max(50).default(20),
    showAll: z.boolean().default(false),
});
export type PreviousMeetingsSearchParams = z.infer<typeof PreviousMeetingsSearchSchema>;

export const meetingsRoute = createRoute({
    getParentRoute: () => portalRoute,
    path: "meetings",
    component: () => <MeetingsLayout />,
});

export const meetingsIndexRoute = createRoute({
    getParentRoute: () => meetingsRoute,
    path: "/",
    component: () => <Meetings />,
    validateSearch: MeetingsIndexSearchSchema,
    search: { middlewares: [stripSearchParams(MeetingsIndexSearchSchema.parse({}))] },
});

export const meetingsPreviousRoute = createRoute({
    getParentRoute: () => meetingsRoute,
    path: "previous",
    component: () => <PreviousMeetings />,
    validateSearch: PreviousMeetingsSearchSchema,
    search: { middlewares: [stripSearchParams(PreviousMeetingsSearchSchema.parse({}))] },
});

export const meetingsSeriesRoute = createRoute({
    getParentRoute: () => meetingsRoute,
    path: "series/$seriesShortCode",
    component: () => <MeetingSeriesDetail />,
});

export const meetingsInstanceRoute = createRoute({
    getParentRoute: () => meetingsRoute,
    path: "$instanceShortCode",
    component: () => <MeetingInstanceDetail />,
});

const vendorRoute = createRoute({
    getParentRoute: () => portalRoute,
    path: "vendors",
    component: () => <VendorLayout />,
});

// export const vendorCreateForm = createRoute({
//     getParentRoute: () => vendorRoute,
//     path: "/new",
//     component: () => <NewVendorForm />,
// });

const rssRoute = createRoute({
    getParentRoute: () => portalRoute,
    path: "rss-feeds",
    component: () => <VendorLayout />,
});

export const rssList = createRoute({
    getParentRoute: () => rssRoute,
    path: "/",
    component: () => <RssList />,
});

export const rssCategoryRoute = createRoute({
    getParentRoute: () => rssRoute,
    path: "category/$categoryName",
    component: () => <FullCategory />,
});

// PRTL-981: Ask Navigator links feed articles as article:{id}; this is where
// the web portal sends them (mobile has had an article page all along).
export const rssArticleRoute = createRoute({
    getParentRoute: () => rssRoute,
    path: "article/$id",
    component: () => <RssArticle />,
});

const digestRoute = createRoute({
    getParentRoute: () => portalRoute,
    path: "digest",
    component: () => <VendorLayout />,
});

export const digestList = createRoute({
    getParentRoute: () => digestRoute,
    path: "/",
    component: () => <AIDigest />,
});

export const digestDetail = createRoute({
    getParentRoute: () => digestRoute,
    path: "/$id",
    component: () => <AIDigestDetail />,
});

const createUserProfileRoute = createRoute({
    getParentRoute: () => portalRoute,
    path: "profile",
    component: () => <Outlet />,
});

export const userProfile = createRoute({
    getParentRoute: () => createUserProfileRoute,
    path: "/$id",
    component: () => <UserProfile />,
});

export const editUserProfile = createRoute({
    getParentRoute: () => createUserProfileRoute,
    path: "/edit/$id",
    component: () => <EditProfile />,
});

export const socialRoute = createRoute({
    getParentRoute: () => portalRoute,
    path: "social",
    component: () => <SocialLayout />,
});

export const socialList = createRoute({
    getParentRoute: () => socialRoute,
    path: "/",
    component: () => <Social />,
});

export const socialPostsOnly = createRoute({
    getParentRoute: () => socialRoute,
    path: "/posts",
    component: () => <SocialMain />,
});

export const socialPost = createRoute({
    getParentRoute: () => socialRoute,
    path: "/post/$id",
    component: () => <SocialRead />,
});

export const socialProfile = createRoute({
    getParentRoute: () => socialRoute,
    path: "/profile/$id",
    component: () => <Profile />,
});

export const liveRoute = createRoute({
    getParentRoute: () => portalRoute,
    path: "/live",
});

const liveListRoute = createRoute({
    getParentRoute: () => liveRoute,
    path: "/",
    component: () => <Live />,
});

// PRTL-507: keyed by the ZoomMeeting row id, not the Zoom meeting number.
export const liveJoinRoute = createRoute({
    getParentRoute: () => liveRoute,
    path: "/$id/join",
    component: () => <LiveJoin />,
});

const chartsRoute = createRoute({
    getParentRoute: () => portalRoute,
    path: "visualizations",
    component: () => <Outlet />,
});

export const chartsListRoute = createRoute({
    getParentRoute: () => chartsRoute,
    path: "/",
    component: () => <ChartsListScreen />,
    validateSearch: ChartsListSchema,
    search: { middlewares: [stripSearchParams(ChartsListSchema.parse({}))] },
});

export const chartNewRoute = createRoute({
    getParentRoute: () => chartsRoute,
    path: "/new",
    component: () => <CreateChartScreen />,
});

export const chartDetailRoute = createRoute({
    getParentRoute: () => chartsRoute,
    path: "/$id",
    component: () => <Outlet />,
});

export const chartViewRoute = createRoute({
    getParentRoute: () => chartDetailRoute,
    path: "/",
    component: () => <ChartDetailScreen />,
});

export const chartEditRoute = createRoute({
    getParentRoute: () => chartDetailRoute,
    path: "/edit",
    component: () => <ChartDetailScreen />,
});

export const chartVersionRoute = createRoute({
    getParentRoute: () => chartDetailRoute,
    path: "/$versionId",
    component: () => <ChartDetailScreen />,
});

export const chartVersionEditRoute = createRoute({
    getParentRoute: () => chartVersionRoute,
    path: "/edit",
    component: () => <ChartDetailScreen />,
});

/**
 * Messages
 */

export const messagesRoute = createRoute({
    getParentRoute: () => portalRoute,
    path: "messages",
    component: () => <Outlet />,
});

export const messagesHomeRoute = createRoute({
    getParentRoute: () => messagesRoute,
    path: "/",
    component: () => <Messages />,
});

export const messagesUserRoute = createRoute({
    getParentRoute: () => messagesRoute,
    path: "/$id",
    component: () => <Messages />,
});

/**
 * Auth
 */
export const loginRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "login",
    component: () => <Login />,
});

const logoutRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "logout",
    component: () => <Logout />,
});

// PRTL-1348: masquerade exchange landing (code arrives in the URL fragment).
const masqueradeRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "masquerade",
    component: () => <Masquerade />,
});

const forgotPasswordRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "forgot-password",
    component: () => <ForgotPassword />,
});

export const resetPasswordRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: "reset-password",
    component: () => <ResetPassword />,
    validateSearch: (search) => {
        if (typeof search.token !== "string") {
            throw new Error("Missing reset token");
        }
        return { token: search.token };
    },
});

const benchmarkRoute = createRoute({
    getParentRoute: () => portalRoute,
    path: "benchmark",
    component: () => <VendorLayout />,
});

export const benchmarkList = createRoute({
    getParentRoute: () => benchmarkRoute,
    path: "/",
    component: () => <BenchMark />,
});

const budgetRoute = createRoute({
    getParentRoute: () => portalRoute,
    path: "budget",
    component: () => <VendorLayout />,
});

export const budgetList = createRoute({
    getParentRoute: () => budgetRoute,
    path: "/",
    component: () => <Budget />,
});

export const resolveNotificationRoute = createRoute({
    getParentRoute: () => portalRoute,
    path: "n/$token",
    component: () => <ResolveNotification />,
});

export const notificationsRoute = createRoute({
    getParentRoute: () => portalRoute,
    path: "notifications",
    component: () => <NotificationsPage />,
});

const routeTree = rootRoute.addChildren([
    portalRoute.addChildren([
        // homeRoute,
        favoritesRoute.addChildren([
            favoritesIndexRoute,
            favoritesPrivateRoute,
            favoritesPublicRoute,
        ]),
        trendingRoute,
        resourceCategoryRoute,
        resourcePostRoute,
        authorPostsRoute,
        discussionsRoute.addChildren([
            discussionsList,
            // discussionsNewRoute,
            discussionsReadRoute,
        ]),
        documentsRoute,
        settingsRoute.addChildren([settingsSecurity, settingsNotifications, settingsCalendars]),
        socialRoute.addChildren([socialList, socialPostsOnly, socialPost, socialProfile]),
        friendRoute.addChildren([friendList]),
        vendorRoute.addChildren([vendorList, vendorDetail]),
        digestRoute.addChildren([digestList, digestDetail]),
        benchmarkRoute.addChildren([benchmarkList]),
        budgetRoute.addChildren([budgetList]),
        rssRoute.addChildren([rssList, rssCategoryRoute, rssArticleRoute]),
        createUserProfileRoute.addChildren([userProfile, editUserProfile]),
        messagesRoute.addChildren([messagesHomeRoute, messagesUserRoute]),
        resolveNotificationRoute,
        notificationsRoute,
        eventsRoute.addChildren([eventsListRoute, eventsDetailRoute, eventsJoinRoute]),
        chartsRoute.addChildren([
            chartsListRoute,
            chartNewRoute,
            chartDetailRoute.addChildren([
                chartViewRoute,
                chartEditRoute,
                chartVersionRoute.addChildren([chartVersionEditRoute]),
            ]),
        ]),
        compassEventsRoute.addChildren([compassEventsIndexRoute, compassEventsJoinRoute]),
        meetingsRoute.addChildren([
            meetingsIndexRoute,
            meetingsPreviousRoute,
            meetingsSeriesRoute,
            meetingsInstanceRoute,
        ]),
        liveRoute.addChildren([liveListRoute, liveJoinRoute]),
    ]),
    loginRoute,
    logoutRoute,
    masqueradeRoute,
    resetPasswordRoute,
    forgotPasswordRoute,
]);

export const router = createRouter({
    routeTree,
    // Replaces the beta's `path: "*"` catch-all route.
    defaultNotFoundComponent: () => <NotFound />,
});

declare module "@tanstack/react-router" {
    interface Register {
        router: typeof router;
    }
}
